import type { DTOBlock } from '@/api/generated-catch-backend/model/dTOBlock';
import { PortableText, isPortableTextEmpty } from '@/app/components/questionnaireV2/components/portable-text';
import CloseIcon from '@/app/components/questionnaireV2/icons/close-icon';
import { motion } from 'motion/react';
import React from 'react';

export interface WhyWeAskProps {
  explanation?: DTOBlock[];
  onClick?: () => void;
  onExpand?: () => void;
  onClose?: () => void;
  className?: string;
  isExpanded?: boolean;
  isFullScreen?: boolean;
}

export const WhyWeAsk: React.FC<WhyWeAskProps> = ({
  explanation,
  onClick,
  onExpand,
  onClose,
  className = '',
  isExpanded = false,
  isFullScreen = false,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (onExpand) {
      onExpand();
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  if (isPortableTextEmpty(explanation)) return null;

  if (isFullScreen && isExpanded) {
    // Full screen expanded view
    return (
      <motion.div
        key="why-we-ask-expanded"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="why-we-ask-expanded-container"
      >
        {/* Header with title and close button */}
        <div className="why-we-ask-header">
          <div className="flex-1" />
          <motion.div layoutId="why-we-ask-title" className="flex flex-1 justify-center">
            <h2 className="why-we-ask-title">Why we ask</h2>
          </motion.div>
          <div className="flex flex-1 justify-end">
            <motion.button
              type="button"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              onClick={handleClose}
              className="btn-open-why-we-ask"
            >
              <CloseIcon />
            </motion.button>
          </div>
        </div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1, ease: 'easeInOut' }}
          className="why-we-ask-content"
        >
          <PortableText content={explanation} className="why-we-ask-explanation" />
        </motion.div>

        {/* Footer with close button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, delay: 0.2, ease: 'easeInOut' }}
          className="why-we-ask-footer pb-safe-offset-24 md:pb-safe-offset-4"
        >
          <button type="button" onClick={handleClose} className="btn-why-we-ask">
            Close
          </button>
        </motion.div>
      </motion.div>
    );
  }

  // Collapsed button view
  return (
    <div className={`why-we-ask-container ${className}`}>
      <button onClick={handleClick} type="button" className="why-we-ask-underlined-text">
        Why we ask
      </button>
    </div>
  );
};

export default WhyWeAsk;
